import type {
  EventKind,
  GetProcessDraftResponse,
  ProcessDraftEventNode
} from '@bliks-io/contracts';

/**
 * The draft editor's event shape, mapped from the canonical draft event rows
 * (`GetProcessDraftResponse.events`).
 *
 * `_id` here is the ROW's `stableStepId`, not its Mongo `_id`. The stable id
 * is the event's identity everywhere the editor touches it:
 *  - it's what step `inputs[].eventId` reference,
 *  - it's the canvas node id (the flow builder keys channel entries by `_id`),
 *  - it's the id the draft event endpoints route by,
 *  - it survives draft↔publish (the live event node's `_id` equals it), so
 *    saved layout positions and undo references stay valid across publishes.
 * The row `_id` is draft-internal storage identity and is deliberately not
 * exposed to the editor.
 */
export interface DraftEditorEvent {
  _id: string;
  nodeKind: 'event';
  eventKind: EventKind;
  eventType: string;
  name: string;
  description: string;
}

export function toDraftEditorEvent(row: ProcessDraftEventNode): DraftEditorEvent {
  return {
    _id: String(row.stableStepId),
    nodeKind: 'event',
    eventKind: row.eventKind,
    eventType: row.eventType ?? 'none',
    name: row.name ?? '',
    description: row.description ?? ''
  };
}

export function draftEditorEventsOf(
  data: Pick<GetProcessDraftResponse, 'events'> | null | undefined
): DraftEditorEvent[] {
  return (data?.events ?? []).map(toDraftEditorEvent);
}
